import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { UpdateContract, UpdateContractVariables } from '@/graphql/contracts/_generated_/updateContract.mutation';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import UpdateContractMutation from '@/graphql/contracts/updateContract.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { NumberUtils } from '@/utils/numbers.utils';
import { fieldVerificationRefreshTrigger } from '../../ContractIq/ContractIq.state';

export type UpdateContractInput = Omit<UpdateContractVariables['input'], 'contractId'>;

export default function useUpdateContract(contractId: string) {
  const setRefreshTrigger = useSetRecoilState(fieldVerificationRefreshTrigger);

  const [_updateContract, { loading }] = useMutation<UpdateContract, UpdateContractVariables>(
    UpdateContractMutation,
    {
      client: apolloClientContracts,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        }
      ],
      onCompleted: () => {
        notificationService.open({
          title: 'Update Contract headers',
          text: 'Contract headers successfully updated',
          type: 'success'
        });
        setRefreshTrigger((prev) => prev + 1);
      },
    },
  );

  const updateContract = async ({
    contractStartDate,
    renewableTerm,
    contractEndDate,
    externalContractID,
    initialTerm }: UpdateContractInput, cbOnError?: () => void) => {
    const nullValues = [null, undefined, ''];
    const renewableTermValue = typeof renewableTerm !== 'number' && nullValues.includes(renewableTerm)
      ? undefined : NumberUtils.parseIntegerInput(renewableTerm?.toString()!);

    const initialTermValue = typeof initialTerm !== 'number' && nullValues.includes(initialTerm)
      ? undefined : NumberUtils.parseIntegerInput(initialTerm?.toString()!);

    _updateContract({
      variables: {
        input: {
          contractId,
          contractStartDate,
          contractEndDate,
          renewableTerm: renewableTermValue,
          externalContractID,
          initialTerm: initialTermValue
        }
      },
      onError: (err) => {
        notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        });

        cbOnError?.();
      }
    });

  };

  return {
    updateContract,
    isLoadingUpdateContract: loading
  } as const;
}
